import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { date, todayIE } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, GraduationCap } from "lucide-react";
import { Badge } from "@/components/hr/HrBits";

const STATUSES = ["not_started", "in_progress", "complete"];
const blankPhases = () => Array.from({ length: 7 }, (_, i) => ({ phase: i + 1, off_the_job: (i + 1) % 2 === 0, status: "not_started", start_date: "", end_date: "", college_block_start: "", college_block_end: "" }));

function Details({ a, setA, staff, hr }) {
  const inp = (k, l, t = "text") => (
    <div><Label className="text-xs">{l}</Label><Input type={t} value={a[k] || ""} disabled={!hr} onChange={(e) => setA({ ...a, [k]: e.target.value })} data-testid={`appr-${k}`} className="mt-1 h-11" /></div>
  );
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      {inp("trade", "Trade")}{inp("solas_reg", "SOLAS registration no.")}{inp("college", "College / training centre")}
      {inp("start_date", "Start date", "date")}{inp("expected_finish", "Expected finish", "date")}
      <div><Label className="text-xs">Mentor</Label>
        <Select value={a.mentor_id || ""} onValueChange={(v) => setA({ ...a, mentor_id: v })} disabled={!hr}>
          <SelectTrigger data-testid="appr-mentor" className="mt-1 h-11"><SelectValue placeholder="Select mentor" /></SelectTrigger>
          <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select></div>
    </div>
  );
}

function Phases({ a, setA, hr }) {
  const set = (i, k, v) => setA({ ...a, phases: a.phases.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  return (
    <div className="space-y-2 mt-5" data-testid="appr-phases">
      {a.phases.map((p, i) => (
        <div key={p.phase} className="rounded-lg border border-border p-3 grid gap-2 sm:grid-cols-[110px_150px_1fr_1fr] items-end" data-testid={`appr-phase-${p.phase}`}>
          <div><div className="font-heading font-bold">Phase {p.phase}</div><div className="text-[10px] font-mono uppercase text-muted-foreground">{p.off_the_job ? "Off-the-job" : "On-the-job"}</div></div>
          {hr ? (
            <Select value={p.status} onValueChange={(v) => set(i, "status", v)}>
              <SelectTrigger data-testid={`appr-phase-status-${p.phase}`} className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          ) : <Badge status={p.status} />}
          <div className="flex gap-1">
            <Input type="date" value={p.start_date} disabled={!hr} onChange={(e) => set(i, "start_date", e.target.value)} className="h-9" title="Phase start" />
            <Input type="date" value={p.end_date} disabled={!hr} onChange={(e) => set(i, "end_date", e.target.value)} className="h-9" title="Phase end" />
          </div>
          {p.off_the_job ? (
            <div className="flex gap-1 items-center"><GraduationCap className="w-4 h-4 text-primary shrink-0" />
              <Input type="date" value={p.college_block_start} disabled={!hr} onChange={(e) => set(i, "college_block_start", e.target.value)} className="h-9" title="College block start" data-testid={`appr-block-start-${p.phase}`} />
              <Input type="date" value={p.college_block_end} disabled={!hr} onChange={(e) => set(i, "college_block_end", e.target.value)} className="h-9" title="College block end" />
            </div>
          ) : <div />}
        </div>
      ))}
    </div>
  );
}

export function Logbook({ uid, entries, canSign, reload }) {
  const [f, setF] = useState({ date: todayIE(), skill: "", notes: "" });
  const add = async () => {
    try { await api.post(`/hr/apprentices/${uid}/logbook`, f); toast.success("Logbook entry added"); setF({ ...f, skill: "", notes: "" }); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const sign = async (e) => { try { await api.post(`/hr/logbook/${e.id}/signoff`); toast.success("Signed off"); reload(); } catch (err) { toast.error(apiErr(err)); } };
  return (
    <div className="mt-6">
      <h3 className="font-heading font-bold uppercase text-sm mb-2">Logbook</h3>
      <div className="card-industrial p-3 grid gap-2 sm:grid-cols-[150px_1fr_1fr_auto] mb-3">
        <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="log-date" className="h-11" />
        <Input placeholder="Skill / task" value={f.skill} onChange={(e) => setF({ ...f, skill: e.target.value })} data-testid="log-skill" className="h-11" />
        <Textarea rows={1} placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="log-notes" />
        <Button onClick={add} disabled={!f.skill} data-testid="log-add" className="h-11 font-bold uppercase">Add</Button>
      </div>
      <div className="card-industrial divide-y divide-border" data-testid="logbook-list">
        {entries.length === 0 && <div className="p-4 text-sm text-muted-foreground">No entries yet.</div>}
        {entries.map((e) => (
          <div key={e.id} className="p-3 flex items-center justify-between gap-3" data-testid={`log-row-${e.id}`}>
            <div><div className="text-sm font-semibold">{e.skill}</div><div className="text-xs text-muted-foreground">{date(e.date)} · {e.created_by}{e.notes ? ` · ${e.notes}` : ""}</div></div>
            {e.signed_off_by ? <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />{e.signed_off_by}</span>
              : canSign ? <Button size="sm" onClick={() => sign(e)} data-testid={`log-sign-${e.id}`}>Sign off</Button> : <Badge status="pending" />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ApprenticePanel({ uid, hr }) {
  const [a, setA] = useState(null);
  const [exists, setExists] = useState(false);
  const [staff, setStaff] = useState([]);
  const load = useCallback(() => {
    api.get(`/hr/apprentices/${uid}`).then((r) => { setExists(!!r.data); setA(r.data || { trade: "", phases: blankPhases(), logbook: [] }); });
  }, [uid]);
  useEffect(() => { load(); if (hr) api.get("/users").then((r) => setStaff(r.data.filter((s) => s.id !== uid))); }, [load, hr, uid]);
  const save = async () => {
    try { await api.put(`/hr/apprentices/${uid}`, a); toast.success(exists ? "Apprentice record saved" : "Apprentice record created"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!a) return null;
  if (!exists && !hr) return <div className="text-center py-8 text-sm text-muted-foreground">No apprentice record.</div>;
  return (
    <div data-testid="apprentice-panel">
      <div className="card-industrial p-5">
        {!exists && <p className="text-sm text-muted-foreground mb-3">Not set up as an apprentice yet. Fill in the details and save to create the record.</p>}
        <Details a={a} setA={setA} staff={staff} hr={hr} />
        <Phases a={a} setA={setA} hr={hr} />
        {hr && <Button onClick={save} data-testid="appr-save" className="mt-4 h-11 font-bold uppercase">{exists ? "Save apprentice record" : "Create apprentice record"}</Button>}
      </div>
      {exists && <Logbook uid={uid} entries={a.logbook || []} canSign={hr} reload={load} />}
    </div>
  );
}
